import { apiClient } from "./client";
import { DestinatarioProposto, EsitoInvio } from "./report";

export interface ModuloRIT {
  id: string;
  intervento_id: string | null;
  data_richiesta: string | null;
  referente_evo: string | null;
  attivita: string | null;
  prodotto: string | null;
  cliente_nome: string | null;
  indirizzo: string | null;
  data_appuntamento: string | null;
  note_info: string | null;
  svolgimento_attivita: string | null;
  ditta_esecutrice: string | null;
  tecnico_nome: string | null;
  data_completamento: string | null;

  /**
   * Il rapportino compilato dal centro assistenza — non lo genera l'app, viene
   * caricato. Sul disco e' sempre un PDF: le foto vengono convertite al
   * caricamento. `null` finche' nessuno ne ha caricato uno.
   */
  file_url_esterno: string | null;
  /** Il nome del file come e' arrivato, o "N foto" se erano piu' d'una. */
  nome_file_esterno: string | null;
  /** Il MIME di PARTENZA: dice se e' arrivato gia' PDF o e' nato da foto. */
  mime_type_esterno: string | null;
  /** Peso del PDF salvato — serve a decidere se sta in un'email. */
  size_bytes_esterno: number | null;
  caricato_il: string | null;

  created_at: string;
  updated_at: string;
}

/**
 * Il tetto oltre il quale Microsoft Graph rifiuta un allegato.
 *
 * E' scritto anche lato server (`LIMITE_ALLEGATI_BYTE` in
 * `services/emailService.ts`), che resta l'unico posto dove la regola viene
 * FATTA RISPETTARE: qui serve solo a decidere se proporre o no l'unione prima
 * di provarci, cosi' chi spedisce lo sa prima invece di leggerlo in un errore.
 * Se il numero cambia di la', qui si sbaglia solo la proposta, non l'esito.
 */
export const LIMITE_ALLEGATO_EMAIL_BYTE = 3 * 1024 * 1024;

type EntityType = "intervento" | "installation";

export async function getModuloRIT(entityType: EntityType, entityId: string): Promise<{ modulo: ModuloRIT | null }> {
  const { data } = await apiClient.get<{ modulo: ModuloRIT | null }>("/moduli-rit", {
    params: { entity_type: entityType, entity_id: entityId },
  });
  return data;
}

export async function createModuloRIT(
  entityType: EntityType,
  entityId: string,
  payload: Partial<ModuloRIT>
): Promise<{ modulo: ModuloRIT }> {
  const { data } = await apiClient.post<{ modulo: ModuloRIT }>("/moduli-rit", {
    entity_type: entityType,
    entity_id: entityId,
    ...payload,
  });
  return data;
}

export async function updateModuloRIT(id: string, payload: Partial<ModuloRIT>): Promise<{ modulo: ModuloRIT }> {
  const { data } = await apiClient.patch<{ modulo: ModuloRIT }>(`/moduli-rit/${id}`, payload);
  return data;
}

/** Scarica/apre il PDF generato al volo dai dati correnti del modulo. */
export async function downloadModuloRITPdf(id: string, filename: string): Promise<void> {
  const response = await apiClient.get(`/moduli-rit/${id}/pdf`, { responseType: "blob" });
  const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}


// ============================================================================
// Invio per email — il PDF viene rigenerato lato server dai dati correnti, lo
// STESSO che si scarica: nessuna copia congelata.
// ============================================================================

/** Gli indirizzi che l'app propone per questo record — possono essere zero. */
export async function destinatariPropostiModuloRIT(id: string): Promise<DestinatarioProposto[]> {
  const { data } = await apiClient.get<{ destinatari: DestinatarioProposto[] }>(`/moduli-rit/${id}/destinatari`);
  return data.destinatari;
}

/**
 * Manda il documento agli indirizzi indicati, uno per destinatario.
 *
 * Risponde 200 anche quando qualche invio fallisce: l'esito e' per destinatario
 * e va letto, non dedotto dallo stato HTTP.
 */
export async function inviaModuloRIT(
  id: string,
  destinatari: string[],
  nota?: string,
  includiEsterno = true
): Promise<EsitoInvio> {
  const { data } = await apiClient.post<EsitoInvio>(`/moduli-rit/${id}/invia`, {
    destinatari,
    nota,
    includi_esterno: includiEsterno,
  });
  return data;
}

// ============================================================================
// Rapportino del centro assistenza — il documento che compila chi va sul posto
// e che il PDF del R.I.T. si porta dietro in coda.
// ============================================================================

/**
 * Carica il documento: un PDF, oppure una o piu' foto dello stesso rapportino
 * (una pagina per foto, nell'ordine in cui vengono passate).
 *
 * Non prende l'id del modulo ma il record a cui appartiene: il documento arriva
 * dal campo e il modulo R.I.T. puo' non essere ancora stato creato. Se non c'e',
 * lo crea il server.
 */
export async function uploadRapportinoEsterno(
  entityType: EntityType,
  entityId: string,
  file: File[]
): Promise<{ modulo: ModuloRIT }> {
  const formData = new FormData();
  for (const f of file) formData.append("file", f);
  formData.append("entity_type", entityType);
  formData.append("entity_id", entityId);

  const { data } = await apiClient.post<{ modulo: ModuloRIT }>("/moduli-rit/rapportino-esterno", formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return data;
}

/**
 * Stacca il documento dal rapportino e lo toglie dal disco. Il modulo R.I.T.
 * resta: si cancella l'allegato, non il rapportino.
 *
 * Serve `rit:documento_delete` (solo admin). Per sostituire un file sbagliato
 * non passa di qui nessuno: basta caricarne un altro.
 */
export async function eliminaRapportinoEsterno(id: string): Promise<{ modulo: ModuloRIT }> {
  const { data } = await apiClient.delete<{ modulo: ModuloRIT }>(`/moduli-rit/${id}/rapportino-esterno`);
  return data;
}
